import React from 'react';
import {
  Button,
  Card,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { AppstoreOutlined, PlusOutlined } from '@ant-design/icons';
import type { AreaTypeItem } from './types';

const { Paragraph } = Typography;

interface AreaTypesTabProps {
  areaTypes: AreaTypeItem[];
  areaTypesLoading: boolean;
  onOpenCreate: () => void;
  onOpenEdit: (areaType: AreaTypeItem) => void;
  onDelete: (areaType: AreaTypeItem) => void;
}

const AreaTypesTab: React.FC<AreaTypesTabProps> = ({
  areaTypes,
  areaTypesLoading,
  onOpenCreate,
  onOpenEdit,
  onDelete,
}) => {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value: string) => value || '—',
    },
    {
      title: 'Scope',
      dataIndex: 'isGlobal',
      key: 'isGlobal',
      render: (value: boolean) =>
        value ? <Tag color="gold">Global</Tag> : <Tag color="blue">Company</Tag>,
    },
    {
      title: 'Space Type',
      dataIndex: 'spaceType',
      key: 'spaceType',
      render: (value: string | null | undefined) => {
        switch (value) {
          case 'outdoor': return <Tag color="green">🌿 Outdoor</Tag>;
          case 'indoor':  return <Tag color="blue">🏢 Indoor</Tag>;
          case 'mixed':   return <Tag color="purple">🔀 Mixed</Tag>;
          default:        return <Tag>Any</Tag>;
        }
      },
    },
    {
      title: 'Max Vertices',
      dataIndex: 'maxVertices',
      key: 'maxVertices',
      render: (value: number | null) => value || '—',
    },
    {
      title: 'Template Size (m)',
      key: 'templateDimensions',
      render: (_: any, record: AreaTypeItem) =>
        record.templateDimensions
          ? `${record.templateDimensions.widthMeters} × ${record.templateDimensions.heightMeters}`
          : '—',
    },
    {
      title: 'Templates',
      key: 'templates',
      render: (_: any, record: AreaTypeItem) => (
        <Space>
          <Tag color={record.templateImage ? 'green' : 'default'}>Image</Tag>
          <Tag color={record.templateLinesImage ? 'green' : 'default'}>Lines</Tag>
        </Space>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      render: (_: any, record: AreaTypeItem) => record.isGlobal ? '—' : record.company?.name || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: AreaTypeItem) => (
        <Space>
          <Button type="link" onClick={() => onOpenEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete area type?"
            description="This cannot be undone. Deletion will fail if the type is used by any areas."
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
            onConfirm={() => onDelete(record)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <AppstoreOutlined />
          Area Types
        </Space>
      }
      bordered={false}
      style={{ background: '#fff' }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Paragraph>
          Manage area types across all companies. Global types are visible to everyone.
        </Paragraph>
        <div>
          <Button type="primary" icon={<PlusOutlined />} onClick={onOpenCreate}>
            New Area Type
          </Button>
        </div>
        <Table
          rowKey="id"
          loading={areaTypesLoading}
          columns={columns}
          dataSource={areaTypes}
          pagination={{ pageSize: 10 }}
        />
      </Space>
    </Card>
  );
};

export default AreaTypesTab;
